import React from 'react';

import { Tooltip, Typography } from 'antd';

import { showDateNoTime } from '@libs/NwMoment';

import { getContactName } from '@hooks/API/Contact/UseContactsList';

import VerticalDate from '@components/Dates/VerticalDate';
import Money from '@components/Gui/Money';
import { NwEditButton } from '@components/Gui/NwButton';
import NwDropDownMenu from '@components/Gui/NwDropDownMenu';
import NwIcon from '@components/Gui/NwIcon';
import ProjectTitle from '@components/Project/ProjectTitle';
import { ClientStatementStatusCell, SubjectStatementStatusCell } from '@components/StatementRowsCommon/cells';
import TransactionTitle from '@components/Transactions/TransactionTitle';

import { getStatementDescription } from '../StatementUtils';
import { ContactNameInTitle, OnHoldTag, PaymentTitleContainer } from './PayableStyles';
import PayableTotalColumn from './PayableTotalColumn';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

/**
 * getPayableColumns
 *
 * Returns column configuration for payable statement table
 */
export const getPayableColumns = ({
    contactType,
    statementType,
    expand,
    showSubjectNameInTitle,
    hasMultipleBillingInfos,
    billingInfoColorMap,
    isItemExpanded,
    handleEditTransaction,
    handlePreviewService,
    handlePreviewProject,
    getInvoiceTypeIcon,
    getInvoiceTypeTooltip,
    getPaymentTooltip,
    isTransactionInvoiced,
    isTransactionPaid,
    isTransactionInNetwalk3Payment,
    isTransactionHold,
    isTransactionNw3Locked,
    getStatementActions,
    onStatusInvoiceClose,
    onStatusPaymentClose,
    setSelectedPayment,
}) => {
    const getSupplierStatusColumnTitle = () => {
        const contactTypeLabel = contactType.charAt(0).toUpperCase() + contactType.slice(1);
        return <div style={{ textAlign: 'center' }}>{`${contactTypeLabel} Status`}</div>;
    };

    return [
        {
            title: () => <div style={{ textAlign: 'center' }}>Date</div>,
            dataIndex: 'Date',
            key: 'Date',
            render: (text, record) => {
                let borderColor = 'transparent';
                if (
                    hasMultipleBillingInfos &&
                    record.Statements &&
                    record.Statements.length > 0 &&
                    record.Statements[0].BillingInfoID &&
                    billingInfoColorMap[record.Statements[0].BillingInfoID]
                ) {
                    borderColor = billingInfoColorMap[record.Statements[0].BillingInfoID];
                }

                const cellStyle = {
                    borderLeft: `6px solid ${borderColor}`,
                    paddingLeft: borderColor !== 'transparent' ? 6 : undefined,
                };

                if (record.isPayment) {
                    return (
                        <div className="date-container" style={cellStyle}>
                            <VerticalDate size="small" date={record.PaymentDate} verticalPadding="0" />
                        </div>
                    );
                }
                return (
                    <div className="date-container" style={cellStyle}>
                        <VerticalDate
                            size="small"
                            date={record.RefDate}
                            cancelled={record.WorkDateCancelled}
                            verticalPadding="0"
                        />
                    </div>
                );
            },
        },
        {
            title: () => <div style={{ textAlign: 'center' }}>Client Status</div>,
            dataIndex: 'ClientInvoiceStatus',
            key: 'ClientInvoiceStatus',
            render: (text, record) => {
                return (
                    <ClientStatementStatusCell
                        transaction={record}
                        detailContactType={contactType}
                        onInvoiceClose={onStatusInvoiceClose}
                        onPaymentClose={onStatusPaymentClose}
                    />
                );
            },
        },
        {
            title: getSupplierStatusColumnTitle(),
            dataIndex: 'Payment',
            key: 'Payment',
            render: (text, record) => (
                <SubjectStatementStatusCell
                    mode="payable-transaction"
                    statementDirection="payable"
                    transaction={record}
                    detailContactType={contactType}
                    getInvoiceIcon={getInvoiceTypeIcon}
                    getInvoiceTooltip={getInvoiceTypeTooltip}
                    getPaymentTooltip={getPaymentTooltip}
                    onInvoiceClose={onStatusInvoiceClose}
                    onPaymentClose={onStatusPaymentClose}
                />
            ),
        },
        {
            title: 'Title',
            key: 'Title',
            width: '30%',
            onCell: (record) => {
                if (record.isPayment) {
                    return {
                        onClick: () => {
                            setSelectedPayment(record.ID);
                        },
                        style: { cursor: 'pointer' },
                    };
                }
                return {
                    onClick: () => {
                        handleEditTransaction(record);
                    },
                    style: { cursor: 'pointer' },
                };
            },
            render: (record) => {
                const subjectName = record.ModelName || record.AgencyName;
                if (record.isPayment) {
                    return (
                        <div className="transaction-details">
                            {showSubjectNameInTitle && subjectName && (
                                <ContactNameInTitle>{subjectName}</ContactNameInTitle>
                            )}
                            <PaymentTitleContainer>
                                <small>Payment</small>
                                <div className="payment-title">{record.PaymentName}</div>
                            </PaymentTitleContainer>
                        </div>
                    );
                }
                return (
                    <div className="transaction-details">
                        {showSubjectNameInTitle && subjectName && (
                            <ContactNameInTitle>{subjectName}</ContactNameInTitle>
                        )}
                        {(contactType === 'agency' || contactType === 'service') && record.ModelID && (
                            <p>{getContactName(record.ModelID)}</p>
                        )}
                        <TransactionTitle transaction={record} target={contactType.toLowerCase()} />
                        {isTransactionHold(record) && <OnHoldTag color="red">On Hold</OnHoldTag>}
                    </div>
                );
            },
        },
        {
            title: 'Project',
            dataIndex: 'Project',
            key: 'Project',
            width: '35%',
            className: 'top-aligned',
            onCell: (record) => {
                if (!record.isPayment && record.Project) {
                    return {
                        onClick: (e) => {
                            e.stopPropagation();
                            handlePreviewProject(record.Project.ID);
                        },
                        style: { cursor: 'pointer' },
                    };
                }
                return {};
            },
            render: (text, record) => {
                if (record.isPayment) {
                    return (
                        <div className="project-details">
                            {record.PaymentNumber && (
                                <>
                                    <small>PAYMENT #</small>
                                    <Typography.Text code>{record.PaymentNumber}</Typography.Text>
                                </>
                            )}
                        </div>
                    );
                }
                return (
                    <div className="project-details">
                        {record.ServiceID && <p className="transaction-client">{getContactName(record.ServiceID)}</p>}
                        {record.Project && <ProjectTitle project={record.Project} compact pointer noClientClick />}
                        <small style={{ display: 'block', whiteSpace: 'pre-wrap' }}>
                            {getStatementDescription(record)}
                        </small>
                    </div>
                );
            },
        },
        {
            title: 'Job Amount',
            dataIndex: 'Numbers',
            key: 'Numbers',
            align: 'right',
            width: '10%',
            onCell: (record) => {
                if (record.isPayment) {
                    return {
                        onClick: () => {
                            setSelectedPayment(record.ID);
                        },
                        style: { cursor: 'pointer' },
                    };
                }
                return {
                    onClick: () => {
                        handleEditTransaction(record);
                    },
                    style: { cursor: 'pointer' },
                };
            },
            render: (text, record) => {
                if (record.isPayment) {
                    return <></>;
                }
                if (record.Family === 'JobFee') {
                    return (
                        <div className="transaction-gross">
                            {(record.Quantity !== 1 || !['Flat', 'Units'].includes(record.TypeQuantity)) && (
                                <p>
                                    <Money amount={record.SingleFee} currency={record.Currency} />
                                    &nbsp;{`x ${record.Quantity} ${record.TypeQuantity}`}
                                </p>
                            )}
                            <p>
                                <Money highlightCurrency amount={record.TotalFee} currency={record.Currency} />
                            </p>
                        </div>
                    );
                } else {
                    return <></>;
                }
            },
        },
        {
            title: 'Net Amount',
            key: 'Amount',
            width: '25%',
            align: 'right',
            onCell: (record) => {
                if (record.isPayment) {
                    return {
                        onClick: () => {
                            setSelectedPayment(record.ID);
                        },
                        style: { cursor: 'pointer' },
                    };
                }
                return {
                    onClick: () => {
                        handleEditTransaction(record);
                    },
                    style: { cursor: 'pointer' },
                };
            },
            render: (record) => {
                if (record.isPayment) {
                    return (
                        <div className="statement-total">
                            <p>
                                <Money
                                    invert
                                    redIfNegative
                                    amount={record.PaymentAmount}
                                    currency={record.PaymentCurrency}
                                />
                            </p>
                        </div>
                    );
                }
                return (
                    <div className="statement-total">
                        <PayableTotalColumn transaction={record} isItemExpanded={isItemExpanded(record.ID)} />
                    </div>
                );
            },
        },
        {
            title: () => <div style={{ textAlign: 'center' }}>Actions</div>,
            dataIndex: 'Actions',
            key: 'Actions',
            render: (text, record) => {
                if (record.isPayment) {
                    return (
                        <div className="transaction-actions">
                            <NwIcon
                                pointer
                                icon={byPrefixAndName.fasl['money-check-dollar-pen']}
                                onClick={() => setSelectedPayment(record.ID)}
                            />
                        </div>
                    );
                }
                const isNw3Locked = isTransactionNw3Locked(record);
                if (isNw3Locked) {
                    return (
                        <div className="transaction-actions">
                            <Tooltip
                                placement="right"
                                title="This line was imported from Netwalk 3 and can't be edited"
                            >
                                <NwIcon disabled icon={byPrefixAndName.fasl['pen-slash']} />
                            </Tooltip>
                        </div>
                    );
                } else {
                    return (
                        <div className="transaction-actions">
                            {!isTransactionPaid(record) && !isTransactionInvoiced(record) && !record.isPayment ? (
                                <NwDropDownMenu
                                    className="transaction-actions-dropdown"
                                    size="small"
                                    actions={getStatementActions(record)}
                                />
                            ) : (
                                <NwEditButton
                                    iconOnly
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditTransaction(record);
                                    }}
                                />
                            )}
                        </div>
                    );
                }
            },
        },
    ];
};

// Helper functions for columns
export const getInvoiceTypeIcon = (transaction, isTransactionInvoicePosted, byPrefixAndName) => {
    if (isTransactionInvoicePosted(transaction)) {
        return byPrefixAndName.fasl['file-export'];
    } else {
        const invoiceid = transaction.Statements.find((st) => st.InvoiceID).InvoiceNumber;
        if (invoiceid) {
            return byPrefixAndName.fasl['file-invoice-dollar'];
        } else {
            return byPrefixAndName.fasl['lock'];
        }
    }
};

export const getInvoiceTypeTooltip = (transaction, contactType) => {
    const contactTypeLabel = contactType.charAt(0).toUpperCase() + contactType.slice(1);
    let text = `${contactTypeLabel} Expenses Invoice`;
    const invoiceid = transaction.Statements.find((st) => st.InvoiceID).InvoiceNumber;
    if (invoiceid) {
        text += ` #${invoiceid} - ${showDateNoTime(transaction.Statements.find((st) => st.InvoiceID).InvoiceDate)}`;
    } else {
        text += ` (Draft)`;
    }
    if (transaction.Statements.some((st) => st.InvoicePosted)) {
        text += ` - Posted to external software`;
    }
    return text;
};

export const getPaymentTooltip = (record, contactType) => {
    const contactTypeLabel = contactType.charAt(0).toUpperCase() + contactType.slice(1);
    const statement = record.Statements.find((st) => st.PaymentID);
    return `${contactTypeLabel} Payment #${statement.PaymentNumber} - ${showDateNoTime(statement.PaymentDate)}`;
};

export const getStatementActions = (
    record,
    handleEditTransaction,
    setTransactionStatementsHoldStatus,
    isTransactionHold,
    byPrefixAndName
) => {
    const actions = [];
    actions.push({
        name: '',
        key: 'edit',
        pinned: true,
        icon: byPrefixAndName.fasl['pen'],
        action: () => handleEditTransaction(record),
    });
    if (isTransactionHold(record)) {
        actions.push({
            name: 'unhold',
            key: 'unhold',
            icon: byPrefixAndName.fasl['eye'],
            action: () => setTransactionStatementsHoldStatus(record, false),
        });
    } else {
        actions.push({
            name: 'hold',
            key: 'hold',
            icon: byPrefixAndName.fasl['eye-slash'],
            action: () => setTransactionStatementsHoldStatus(record, true),
        });
    }
    return actions;
};
